import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useAdminScope } from '../hooks/useAdminScope';
import { showAdminPath } from '../utils/adminAccess';
import { adminWeeklyReportsApi, type AdminWeeklyReportSummary } from '../api/client';
import { formatWeekRange, shiftWeek, weekEndOf } from '../utils/weeklyReport';

/**
 * 週次レポートの生成状況（課題 #59。2026-09-29 高尾さん決定の案 B）。
 * 生成できた件数・できなかった件数と、使ったプロンプトの版だけを出す。
 * 本文とメールアドレスは出さない（本人への説明の範囲と合わないため。文面の確認は試し打ちで行う）。
 */
export default function AdminWeeklyReports() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const adminScope = useAdminScope();
  const [summary, setSummary] = useState<AdminWeeklyReportSummary | null>(null);
  /** 週次がある最新の週の月曜（「次の週」をそこで止める） */
  const [latestWeek, setLatestWeek] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (weekStart?: string | null) => {
      const token = getToken();
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const res = await adminWeeklyReportsApi.summary(token, weekStart);
        // 週を指定して週次が無い週のときも、指定した週として表示する
        setSummary({ ...res, week_start: res.week_start ?? weekStart ?? null });
        if (!weekStart) setLatestWeek(res.week_start ?? null);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('common.error'));
      } finally {
        setLoading(false);
      }
    },
    [getToken, t]
  );

  useEffect(() => {
    load();
  }, [load]);

  const weekStart = summary?.week_start ?? null;
  const weekEnd = weekStart ? summary?.week_end || weekEndOf(weekStart) : null;
  const total = summary ? summary.generated + summary.failed : 0;
  const versions = summary?.prompt_versions ?? [];

  const versionLabel = (v: string | null | undefined) =>
    v == null || v === ''
      ? t('admin.weeklyReports.versionUnknown')
      : v === 'default'
      ? t('admin.weeklyReports.versionDefault')
      : v;

  return (
    <div className="page admin-weekly-reports-page">
      <h1>{t('admin.weeklyReports.title')}</h1>
      <p className="admin-page-intro">{t('admin.weeklyReports.intro')}</p>
      <p className="admin-form-hint">{t('admin.weeklyReports.noBodyNote')}</p>
      {adminScope === 'restricted' && (
        <p className="admin-form-hint" data-testid="weekly-reports-scope-note">
          {t('admin.weeklyReports.restrictedNote')}
        </p>
      )}

      {error && (
        <div className="admin-error" role="alert">
          <p>{error}</p>
          <button type="button" className="admin-btn-secondary" onClick={() => load(weekStart)}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {loading && !summary && <p>{t('common.loading')}</p>}

      {summary && !error && (
        <div className="admin-card" data-testid="weekly-reports-summary">
          {weekStart == null ? (
            <p data-testid="weekly-reports-none">{t('admin.weeklyReports.none')}</p>
          ) : (
            <>
              <div className="admin-weekly-nav">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => load(shiftWeek(weekStart, -1))}
                  disabled={loading}
                >
                  {t('admin.weeklyReports.prevWeek')}
                </button>
                <h2 className="admin-weekly-range" data-testid="weekly-reports-range">
                  {t('admin.weeklyReports.weekOf', { range: formatWeekRange(weekStart, weekEnd ?? weekStart) })}
                </h2>
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => load(shiftWeek(weekStart, 1))}
                  disabled={loading || latestWeek == null || weekStart >= latestWeek}
                >
                  {t('admin.weeklyReports.nextWeek')}
                </button>
              </div>
              {total === 0 ? (
                <p data-testid="weekly-reports-empty-week">{t('admin.weeklyReports.emptyWeek')}</p>
              ) : (
                <>
                  <dl className="admin-weekly-counts">
                    <div>
                      <dt>{t('admin.weeklyReports.generated')}</dt>
                      <dd data-testid="weekly-reports-generated">
                        {t('admin.weeklyReports.count', { count: summary.generated })}
                      </dd>
                    </div>
                    <div>
                      <dt>{t('admin.weeklyReports.failed')}</dt>
                      <dd data-testid="weekly-reports-failed">
                        {t('admin.weeklyReports.count', { count: summary.failed })}
                      </dd>
                    </div>
                  </dl>
                  {summary.failed > 0 && <p className="admin-form-hint">{t('admin.weeklyReports.failedNote')}</p>}
                  {versions.length > 0 && (
                    <table className="admin-table" data-testid="weekly-reports-versions">
                      <thead>
                        <tr>
                          <th scope="col">{t('admin.weeklyReports.promptVersion')}</th>
                          <th scope="col">{t('admin.weeklyReports.versionCount')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {versions.map((v) => (
                          <tr key={v.prompt_version ?? '(none)'}>
                            <td>{versionLabel(v.prompt_version)}</td>
                            <td>{t('admin.weeklyReports.count', { count: v.count })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {showAdminPath('/admin/prompts', adminScope) && (
        <p className="admin-form-hint">
          {t('admin.weeklyReports.previewHint')} <Link to="/admin/prompts">{t('admin.weeklyReports.toPrompts')}</Link>
        </p>
      )}
    </div>
  );
}
